import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';

import { CONTENTFUL_COMPONENT } from '../../../shared/constants';
import { FormResult as FormResultType } from '../../../types/shared';

import FormResult from '../../atoms/form-result/FormResult';
import Paper from '../../atoms/paper/Paper';

import {
  FormInputProps,
  renderFormInput,
} from '../../../utils/forms/renderFormInput';
import { ReactMarkdownExtended } from '../../../utils/generic';
import { parseSectionID } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import { H1 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import {
  CalculatorProps,
  DisclaimerProps,
  InputActions,
  InputValue,
  InputValueAction,
  MainResultProps,
  ResultListProps,
} from './Calculator.types';

// Regex that tests on numberic input: 0-9, but also numbers with decimals: 0.1 for example. It disallows for example: 01.
const numbericRegex = /^(0(\.\d+)?|[1-9]\d*(\.\d+)?)$/;

const Calculator = ({
  id,
  title,
  inputs,
  results,
  resultsImage,
  disclaimer,
  trackingName,
  anchorName,
}: CalculatorProps) => {
  const anchorId = parseSectionID(CONTENTFUL_COMPONENT.calculator, anchorName);
  const inputValuesReducer = (
    state: InputValue[],
    action: InputValueAction
  ): InputValue[] => {
    switch (action.type) {
      case InputActions.SET:
        // Check if the value is numeric.
        if (action.value && !numbericRegex.test(action.value)) {
          return state;
        }
        return [
          ...state.filter((item) => item.id !== action.name),
          ...(action.value ? [{ id: action.name, value: action.value }] : []),
        ];
      case InputActions.INITIALIZE:
        return action.initialValues || [];
      default:
        return state;
    }
  };

  const [inputValues, dispatchInputValues] = useReducer(inputValuesReducer, []);

  const handleCalculationChange = (name: string, value: string) => {
    dispatchInputValues({ type: InputActions.SET, name, value });
  };

  useEffect(() => {
    const transformedArray = [...results, ...inputs].map((item) => ({
      id: item.valueId,
      value: '0',
    }));

    dispatchInputValues({
      type: InputActions.INITIALIZE,
      initialValues: transformedArray,
      name: '',
      value: '',
    });
  }, []);

  return (
    <StyledSection id={anchorId}>
      <ContentContainer>
        <StyledH1>{title}</StyledH1>
        <CalculationContainer>
          <SegmentContainer>
            <Paper>
              <StyledForm
                data-testid="calculator-form"
                data-pw-testid="calculator-form"
              >
                {inputs.map((item, index) => {
                  if (item.__typename === CONTENTFUL_COMPONENT.formResult) {
                    return (
                      <FormResult
                        {...(item as FormResultType)}
                        inputValues={inputValues}
                        key={index}
                        onCalculate={handleCalculationChange}
                      />
                    );
                  }

                  const inputProps = {
                    ...item,
                    onChange: handleCalculationChange,
                  } as FormInputProps;

                  return renderFormInput(inputProps, index);
                })}
              </StyledForm>
            </Paper>
          </SegmentContainer>
          <SegmentContainer>
            {results.length && (
              <>
                <MainResult
                  image={resultsImage}
                  result={results[0]}
                  inputValues={inputValues}
                  onCalculate={handleCalculationChange}
                  trackingData={{ id, trackingName }}
                />
                {!!results.slice(1).length && (
                  <ResultList
                    results={results.slice(1)}
                    inputValues={inputValues}
                    onCalculate={handleCalculationChange}
                  />
                )}
                {disclaimer && <Disclaimer body={disclaimer} />}
              </>
            )}
          </SegmentContainer>
        </CalculationContainer>
      </ContentContainer>
    </StyledSection>
  );
};

const MainResult = ({
  image,
  result,
  inputValues,
  onCalculate,
  trackingData,
}: MainResultProps) => (
  <Paper
    backgroundColor={colors.abnGreen500}
    radiusBottomLeft={0}
    radiusBottomRight={0}
  >
    {image?.file && (
      <StyledImage
        src={image.file.url}
        alt={image.description || 'calculation-result-image'}
      />
    )}
    <FormResult
      {...result}
      inputValues={inputValues}
      theme="large"
      onCalculate={onCalculate}
      trackingData={trackingData}
    />
  </Paper>
);

const ResultList = ({ results, inputValues, onCalculate }: ResultListProps) => (
  <Paper radiusTopLeft={0} radiusTopRight={0}>
    <StyledList>
      {results.map((result, index) => (
        <StyledListItem key={index}>
          <FormResult
            {...result}
            inputValues={inputValues}
            onCalculate={onCalculate}
          />
        </StyledListItem>
      ))}
    </StyledList>
  </Paper>
);

const Disclaimer = ({ body }: DisclaimerProps) => (
  <StyledPaper backgroundColor={colors.abnInformative}>
    <StyledReactMarkdownExtended>
      {body.childMarkdownRemark.rawMarkdownBody}
    </StyledReactMarkdownExtended>
  </StyledPaper>
);

const StyledSection = styled.section`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ContentContainer = styled.div`
  max-width: 50.625rem;

  ${mq.from.breakpoint.XL`
    max-width: 52.5rem;
  `}
`;

const SegmentContainer = styled.div`
  flex: 1;
`;

const StyledH1 = styled(H1)`
  margin-bottom: ${Spacings.XL};
  text-align: center;

  ${mq.from.breakpoint.L`
    margin-bottom: ${Spacings.XXXL};
  `}
`;

const CalculationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.S};

  ${mq.from.breakpoint.M`
    gap: ${Spacings.M};
    flex-direction: row;
  `}
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.M};
`;

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.M};
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledListItem = styled.li`
  margin: 0;
  padding: 0;
`;

const StyledPaper = styled(Paper)`
  margin-top: ${Spacings.S};
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    margin-top: ${Spacings.M};
    padding: ${Spacings.S};
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.S};
  `}
`;

const StyledReactMarkdownExtended = styled(ReactMarkdownExtended)`
  p {
    margin: 0 0 ${Spacings.XXS};
  }

  p:last-child {
    margin: 0;
  }
`;

const StyledImage = styled.img`
  height: ${Spacings.XXXL};
  width: ${Spacings.XXXL};
`;

export default Calculator;
